import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { AppSidebar } from '@/components/app-sidebar'
import { NavRail } from '@/components/nav-rail'
import { SiteHeader } from '@/components/site-header'
import { SpaceMesh } from '@/components/space-mesh'
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import { useRevealAfterLoader } from '@/lib/loader-handoff'
import { rememberSection, resolveSection, type SectionId, sectionForPath } from '@/lib/nav'
import { OverlayHost } from '@/overlays/OverlayHost'
import { usePreferences } from '@/stores/preferences'

/** The app chrome: section rail + collapsible inner menu + header, wrapping page content. */
export function AppShell({ children }: { children: ReactNode }) {
  const sidebarOpen = usePreferences((s) => s.sidebarOpen)
  const setSidebarOpen = usePreferences((s) => s.setSidebarOpen)
  const { pathname } = useLocation()
  // The page content fades in when it takes over from the brand loader; the
  // chrome around it does not (it sits under the fading loader already).
  const scroller = useRef<HTMLDivElement>(null)
  useRevealAfterLoader(scroller)

  // The route owns the selection whenever it can; the module-level memory in
  // lib/nav.ts covers the routes no section claims (Settings) and sections
  // with no destinations yet — it has to live OUTSIDE this component, because
  // every page mounts its own shell and local state would forget the
  // selection on the very navigation it exists to survive.
  const [section, setSection] = useState<SectionId>(() => resolveSection(pathname))
  useEffect(() => {
    const owner = sectionForPath(pathname)
    if (owner) setSection(owner)
  }, [pathname])
  useEffect(() => {
    rememberSection(section)
  }, [section])

  return (
    <SidebarProvider
      open={sidebarOpen}
      onOpenChange={setSidebarOpen}
      // The shell is VIEWPORT-LOCKED: the window never scrolls, the content
      // does, inside the card below the header. When the page scrolled
      // instead, the taller-than-viewport card slid past the viewport-pinned
      // rail and sidebar, and the grounds met in mismatched bands at the
      // bottom of every long page.
      className="h-svh"
      style={
        {
          // 72 (18rem) left the nav a wide empty column beside the content.
          '--sidebar-width': 'calc(var(--spacing) * 60)',
          '--header-height': 'calc(var(--spacing) * 12)',
          '--rail-width': 'calc(var(--spacing) * 14)',
        } as React.CSSProperties
      }
    >
      {/* Behind the h-svh shell: if a mobile browser bar retracts on an
          overscroll gesture, the exposed band matches the ground above it.
          WHICH ground differs by viewport: on a phone the sheet layout drops
          the inset variant and the whole shell is bg-background, so a
          sidebar-coloured band there would be the very mismatch this div
          exists to prevent. */}
      <div aria-hidden className="fixed inset-0 -z-10 bg-background md:bg-sidebar" />
      <NavRail section={section} onSelect={setSection} />
      <AppSidebar variant="inset" section={section} />
      {/* A real border, not a colour cut: the card's edge against the sidebar
          ground is drawn, the same hairline the rail wears. overflow-hidden
          rides with the md: radius it exists to clip — below md there is no
          rounding, and clipping there would eat focus rings at the edge. */}
      <SidebarInset className="relative min-h-0 md:overflow-hidden md:border md:border-sidebar-border">
        {/* The ambient ground sits BEHIND the content and outside the scroller, so
            it never scrolls and never repaints on scroll. It runs in BOTH themes —
            softer and blurrier on paper (see space-mesh.tsx). */}
        <SpaceMesh />
        <div className="relative z-10 flex min-h-0 flex-1 flex-col">
          <SiteHeader />
          {/* A flex column, so page roots that say flex-1 still stretch the
              way they did when the window was the scroller. Trap for future
              pages: a flex item's min-height:auto floors it at CONTENT size,
              so a page root with nothing visible in it (or overflow set)
              can collapse to zero here — give it real content or flex-none. */}
          <div
            ref={scroller}
            data-slot="app-scroller"
            className="flex min-h-0 flex-1 flex-col overflow-y-auto"
          >
            {children}
          </div>
        </div>
      </SidebarInset>
      {/* Mounted with the chrome: every dialog, drawer, confirm and the ⌘K
          palette render here, one at a time (overlays/, #209). It renders
          through a portal, so its place in this tree is about lifetime — inside
          the router and the providers the content needs — not layout. */}
      <OverlayHost />
    </SidebarProvider>
  )
}
